import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { customerApi, formatApiError } from '@/lib/api';
import { BottomNav } from '@/components/customer/BottomNav';
import { NotificationBell } from '@/components/customer/NotificationBell';
import { InstallAppButton } from '@/components/customer/InstallAppButton';
import { TopUpDialog } from '@/components/customer/TopUpDialog';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { RefreshCw, Wallet, Gift } from 'lucide-react';

const STATUS_STYLES = {
  ACTIVE: 'bg-coriander/10 text-coriander',
  PENDING: 'bg-amber-100 text-amber-700',
  PAUSED: 'bg-slate-100 text-slate-600',
  COMPLETED: 'bg-slate-100 text-slate-600',
  EXPIRED: 'bg-red-100 text-red-600',
  CANCELLED: 'bg-red-100 text-red-600',
  SUSPENDED: 'bg-red-100 text-red-600',
};

export default function Account() {
  const { customer } = useAuth();
  const [subscription, setSubscription] = useState(undefined);
  const [qr, setQr] = useState(null);
  const [loadingQr, setLoadingQr] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState('');
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [promo, setPromo] = useState(null);

  const loadSubscription = async () => {
    try {
      const { data } = await customerApi.get('/subscriptions/me');
      setSubscription(data.current);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const loadQr = async () => {
    setLoadingQr(true);
    try {
      const { data } = await customerApi.get('/qr/me');
      setQr(data);
    } catch {
      setQr(null);
    } finally {
      setLoadingQr(false);
    }
  };

  useEffect(() => {
    loadSubscription();
    loadQr();
    customerApi
      .get('/promo')
      .then(({ data }) => setPromo(data.banner))
      .catch(() => setPromo(null));
  }, []);

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const { data } = await customerApi.post('/qr/regenerate');
      setQr(data);
      toast.success('New QR code generated. Your old QR no longer works.');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone pb-24">
      <div className="max-w-md mx-auto">
        <div className="px-6 pt-6 flex justify-center">
          <img src="/logo-wordmark.png" alt="The Tiffin by The Virsa" className="h-10 object-contain" />
        </div>
        <header className="px-6 pt-4 pb-4 flex items-start justify-between">
          <div>
            <p className="text-muted-ink text-sm">Welcome back,</p>
            <h1 className="font-heading text-2xl font-700 text-charcoal" data-testid="account-customer-name">
              {customer?.name}
            </h1>
          </div>
          <NotificationBell />
        </header>

        <section className="px-6 pt-4 pb-6">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-ink mb-2">Current Subscription</h2>
          {subscription === undefined ? (
            <div className="h-32 rounded-2xl bg-white border border-black/5 animate-pulse" />
          ) : subscription ? (
            <div data-testid="subscription-card" className="rounded-2xl bg-white border border-black/5 shadow-sm p-5 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-heading text-lg font-semibold text-charcoal">{subscription.plan?.name}</p>
                  <p className="text-xs text-muted-ink mt-0.5">
                    Expires {new Date(subscription.expiryDate).toLocaleDateString('en-CA')}
                  </p>
                </div>
                <span
                  data-testid="subscription-status-badge"
                  className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_STYLES[subscription.status] || 'bg-slate-100 text-slate-600'}`}
                >
                  {subscription.status}
                </span>
              </div>

              {subscription.status === 'PENDING' && (
                <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2" data-testid="subscription-pending-note">
                  Visit the store to complete payment — your tiffins activate once payment is confirmed.
                </p>
              )}

              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-xl bg-bone py-3">
                  <p className="font-heading text-2xl font-700 text-charcoal" data-testid="subscription-remaining">
                    {subscription.remainingCredits}
                  </p>
                  <p className="text-[11px] text-muted-ink mt-0.5">Remaining</p>
                </div>
                <div className="rounded-xl bg-bone py-3">
                  <p className="font-heading text-2xl font-700 text-charcoal">{subscription.usedCredits}</p>
                  <p className="text-[11px] text-muted-ink mt-0.5">Used</p>
                </div>
                <div className="rounded-xl bg-bone py-3">
                  <p className="font-heading text-2xl font-700 text-charcoal">{subscription.originalCredits}</p>
                  <p className="text-[11px] text-muted-ink mt-0.5">Total</p>
                </div>
              </div>

              {subscription.bonusCredits > 0 && (
                <p className="flex items-center gap-1.5 text-xs text-coriander bg-coriander/10 rounded-lg px-3 py-2" data-testid="subscription-bonus-note">
                  <Gift size={13} /> Includes {subscription.bonusCredits} bonus tiffin{subscription.bonusCredits === 1 ? '' : 's'} from THE TIFFIN
                </p>
              )}
            </div>
          ) : (
            <div className="rounded-2xl bg-white border border-black/5 shadow-sm p-6 text-center">
              <p className="text-charcoal font-medium">No active subscription</p>
              <p className="text-sm text-muted-ink mt-1 mb-4">Your plan has ended — renew to get a fresh set of tiffins.</p>
              <Button
                data-testid="open-renew-dialog-btn"
                onClick={() => setTopUpOpen(true)}
                className="w-full h-11 rounded-full bg-saffron hover:bg-saffron-hover text-white transition-colors duration-150"
              >
                <Wallet size={16} className="mr-2" /> Renew Subscription
              </Button>
            </div>
          )}
        </section>

        <TopUpDialog open={topUpOpen} onOpenChange={setTopUpOpen} currentPlanId={subscription?.plan?.id} />

        {promo && (
          <section className="px-6 pb-6" data-testid="promo-banner">
            <div className="rounded-2xl bg-white border border-black/5 shadow-md overflow-hidden">
              {promo.mediaType === 'VIDEO' ? (
                <video
                  data-testid="promo-banner-video"
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/promo/media?v=${promo.version}`}
                  className="w-full aspect-square object-cover block"
                  autoPlay
                  muted
                  loop
                  playsInline
                />
              ) : (
                <img
                  data-testid="promo-banner-image"
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/promo/media?v=${promo.version}`}
                  alt="The Tiffin promotion"
                  className="w-full aspect-square object-cover block"
                />
              )}
            </div>
          </section>
        )}

        <section className="px-6 pb-6">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-ink mb-2">Subscription QR</h2>
          <div className="rounded-2xl bg-white border border-black/5 shadow-md overflow-hidden" data-testid="qr-card">
            {loadingQr ? (
              <div className="h-80 bg-bone flex items-center justify-center animate-pulse">
                <p className="text-xs text-muted-ink" data-testid="qr-generating-label">Generating your QR…</p>
              </div>
            ) : qr?.qrImage ? (
              <>
                <img src={qr.qrImage} alt="THE TIFFIN pickup QR code" className="w-full" data-testid="qr-image" />
                <div className="px-5 py-4 flex items-center justify-between gap-3">
                  <p className="text-xs text-muted-ink">
                    Save a screenshot. This QR stays valid until you generate a new one.
                  </p>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid="generate-new-qr-btn"
                        className="shrink-0 rounded-full border-saffron text-saffron hover:bg-saffron/10 transition-colors duration-150"
                      >
                        <RefreshCw size={14} className="mr-1.5" /> New QR
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent data-testid="regenerate-qr-dialog">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Generate a new QR code?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Your current QR code (including any saved screenshots) will stop working immediately.
                          Only your newest QR code will be valid at pickup.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid="regenerate-qr-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          data-testid="regenerate-qr-confirm"
                          disabled={regenerating}
                          onClick={regenerate}
                          className="bg-saffron hover:bg-saffron-hover"
                        >
                          {regenerating ? 'Generating…' : 'Yes, generate new QR'}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </>
            ) : (
              <div className="p-8 text-center text-sm text-muted-ink">
                No QR code available. You'll get one once your subscription is active.
              </div>
            )}
          </div>
        </section>

        {error && <p className="px-6 text-sm text-destructive">{error}</p>}

        <div className="px-6 pt-2 pb-6 flex justify-center">
          <InstallAppButton className="h-10 text-sm" />
        </div>
      </div>
      <BottomNav />
    </div>
  );
}
